import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Clock, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FRUIT_VISUAL, getRipenessStage, type TreeFruit } from './types';
import { formatDistanceToNow } from 'date-fns';

interface FruitModalProps {
  fruit: TreeFruit | null;
  onClose: () => void;
  onShare?: (fruit: TreeFruit) => void;
}

const RIPENESS_LABELS: Record<string, string> = {
  new: '🌱 Freshly planted',
  growing: '🌿 Growing',
  ripe: '✨ Ripened',
  golden: '🌟 Golden legacy',
};

export function FruitModal({ fruit, onClose, onShare }: FruitModalProps) {
  if (!fruit) return null;

  const visual = FRUIT_VISUAL[fruit.template] || FRUIT_VISUAL.custom;
  const ripeness = getRipenessStage(fruit.createdAt);
  const timeAgo = formatDistanceToNow(new Date(fruit.createdAt), { addSuffix: true });

  return (
    <AnimatePresence>
      {fruit && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-md rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, #0d1f17 0%, #132b1e 50%, #0a1810 100%)',
              border: '1px solid rgba(255, 215, 100, 0.15)',
              boxShadow: '0 0 60px rgba(255, 200, 60, 0.1), 0 25px 50px rgba(0,0,0,0.5)',
            }}
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 20 }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="px-6 pt-6 pb-4 text-center">
              <motion.span
                className="text-5xl block mb-3"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {visual.emoji}
              </motion.span>
              <h3 className="text-xl font-bold text-white mb-1">
                To {fruit.recipientName}
              </h3>
              <p className="text-sm text-amber-300/70 flex items-center justify-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {timeAgo}
              </p>
              <p className="text-xs text-emerald-400/60 mt-1">
                {RIPENESS_LABELS[ripeness]}
              </p>
            </div>

            {/* Message */}
            <div className="px-6 pb-4">
              <div
                className="rounded-2xl p-4 text-white/90 text-sm leading-relaxed max-h-48 overflow-y-auto"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {fruit.message.length > 400
                  ? fruit.message.slice(0, 400) + '…'
                  : fruit.message}
              </div>
            </div>

            {/* Category badge */}
            <div className="px-6 pb-4 flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  background: `${visual.color}22`,
                  color: visual.color,
                  border: `1px solid ${visual.color}33`,
                }}
              >
                <Heart className="h-3 w-3" />
                {visual.label}
              </span>
              <span className="text-xs text-white/70 capitalize">
                {fruit.relationship.replace('_', ' ')}
              </span>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              {fruit.publicSlug && (
                <Button
                  size="sm"
                  className="flex-1 gap-2 rounded-xl bg-amber-500/90 hover:bg-amber-500 text-black font-semibold"
                  onClick={() => onShare?.(fruit)}
                >
                  <Share2 className="h-4 w-4" />
                  Share Message
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-2 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/15"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
